/**
 * WorkspaceSettingWorkflows — Workflow permissions management tab.
 *
 * sl-za0v.10.3: Dedicated tab in Workspace Settings for managing
 * per-workflow permissions. Lists all workflows in the workspace,
 * allows selecting one, and shows the PermissionEditor for it.
 *
 * sl-auz2.2.2: Visible to all roles. Non-admins see workflow list
 * but cannot manage permissions (PermissionEditor hidden).
 *
 * sl-bj0x.1: Refactored to use shared permissions/styles.css
 * (expand-in-place pattern, consistent with docs/folders/credentials).
 */
import { notify } from '@affine/component';
import { FeatureUpgradeNudge } from '@affine/core/components/affine/feature-gate';
import { useBackendAvailable } from '@affine/core/components/hooks/use-backend-availability';
import { useWorkspaceInfo } from '@affine/core/components/hooks/use-workspace-info';
import { useFeatureGate } from '@affine/core/modules/streamlain/hooks/use-feature-gate';
import { WorkspaceService } from '@affine/core/modules/workspace';
import { Permission } from '@affine/graphql';
import { useI18n } from '@affine/i18n';
import { useService } from '@toeverything/infra';
import { useCallback, useEffect, useMemo, useState } from 'react';

import {
  SettingsOfflineHeaderAction,
  useSettingsOfflineIncompleteLabel,
} from '../../shared/settings-offline-status';
import { SettingsEditorScopeHint } from '../shared/settings-editor-scope-hint';
import {
  SettingsBadge,
  SettingsEntityList,
  SettingsEntityMessage,
  SettingsExpandableItem,
  SettingsMetaItem,
} from '../shared/settings-entity';
import { SettingsSearchableEntityPage } from '../shared/settings-entity-page';
import { requestWorkspaceSetting } from '../shared/workspace-settings-api';
import { WorkflowPermissionsEditor } from './workflow-permissions-editor';

// =============================================================================
// Types
// =============================================================================

interface WorkflowListItem {
  id: string;
  name: string;
  stepCount: number;
  active: boolean;
}

// =============================================================================
// Hook: Load workflows from SWR API
// =============================================================================

async function fetchWorkflowPermissions(
  workspaceId: string,
  workflowId: string
): Promise<Set<string>> {
  const res = await requestWorkspaceSetting(
    workspaceId,
    `/api/workspace/${workspaceId}/workflow/${workflowId}/my-permissions`,
    undefined
  );
  if (!res.ok) return new Set();
  const data = (await res.json()) as { permissions?: string[] };
  return new Set(data.permissions ?? []);
}

function useWorkflowList(isAdmin: boolean, backendAvailable: boolean) {
  const workspaceService = useService(WorkspaceService);
  const workspaceId = workspaceService.workspace.id;
  const [workflows, setWorkflows] = useState<WorkflowListItem[]>([]);
  const [loading, setLoading] = useState(true);

  const refresh = useCallback(async () => {
    setLoading(true);
    if (!backendAvailable) {
      setLoading(false);
      return;
    }
    try {
      const res = await requestWorkspaceSetting(
        workspaceId,
        `/api/workspace/${workspaceId}/workflow/workflows`,
        undefined
      );
      if (!res.ok) throw new Error(`HTTP ${res.status}`);
      const data = await res.json();
      // Controller returns { configured: boolean, workflows: [...] }
      const items = (data.workflows ?? data) as WorkflowListItem[];
      if (isAdmin) {
        setWorkflows(items);
        return;
      }

      const visible = await Promise.all(
        items.map(async item => {
          const permissions = await fetchWorkflowPermissions(
            workspaceId,
            item.id
          );
          return permissions.has('workflow.manage_permissions') ? item : null;
        })
      );
      setWorkflows(visible.filter((item): item is WorkflowListItem => !!item));
    } catch (err) {
      notify.error({
        title: `Failed to load workflows: ${err instanceof Error ? err.message : 'Unknown error'}`,
      });
    } finally {
      setLoading(false);
    }
  }, [backendAvailable, isAdmin, workspaceId]);

  useEffect(() => {
    void refresh();
  }, [refresh]);

  return { workflows, loading, refresh };
}

// =============================================================================
// Component
// =============================================================================

const WorkflowAccessTab = ({
  workflowId,
  hasWorkflowRbac,
  featureName,
}: {
  workflowId: string;
  hasWorkflowRbac: boolean;
  featureName: string;
}) => {
  if (!hasWorkflowRbac) {
    return (
      <FeatureUpgradeNudge
        featureKey="workflow-rbac"
        featureName={featureName}
        requiredPlan="Team"
      />
    );
  }

  return <WorkflowPermissionsEditor workflowId={workflowId} />;
};

const WorkflowListEntry = ({
  workflow,
  expanded,
  onToggle,
  hasWorkflowRbac,
}: {
  workflow: WorkflowListItem;
  expanded: boolean;
  onToggle: () => void;
  hasWorkflowRbac: boolean;
}) => {
  const t = useI18n();

  const featureName =
    t['com.streamlain.settings.workspace.workflows.heading']?.() ??
    'Workflow rights';

  return (
    <SettingsExpandableItem
      dataTestId={`workflow-permissions-item-${workflow.id}`}
      expanded={expanded}
      onToggle={onToggle}
      title={workflow.name}
      badges={
        <SettingsBadge variant={workflow.active ? 'active' : 'error'}>
          {workflow.active
            ? (t[
                'com.streamlain.settings.workspace.workflows.meta.active'
              ]?.() ?? 'Einsatzbereit')
            : (t[
                'com.streamlain.settings.workspace.workflows.meta.inactive'
              ]?.() ?? 'Entwurf')}
        </SettingsBadge>
      }
      meta={
        <>
          <SettingsMetaItem>
            {`${workflow.stepCount} ${t['com.streamlain.settings.workspace.workflows.meta.steps']?.() ?? 'Schritte'}`}
          </SettingsMetaItem>
          <SettingsMetaItem>
            {`${t['com.streamlain.settings.workspace.workflows.meta.id']?.() ?? 'Workflow-ID'} ${workflow.id.slice(0, 12)}...`}
          </SettingsMetaItem>
        </>
      }
    >
      <WorkflowAccessTab
        workflowId={workflow.id}
        hasWorkflowRbac={hasWorkflowRbac}
        featureName={featureName}
      />
    </SettingsExpandableItem>
  );
};

export const WorkspaceSettingWorkflows = () => {
  const t = useI18n();
  const backendAvailable = useBackendAvailable();
  const offlineLabel = useSettingsOfflineIncompleteLabel();
  const workspaceService = useService(WorkspaceService);
  const information = useWorkspaceInfo(workspaceService.workspace);
  const isAdmin = !!(information?.isOwner || information?.isAdmin);
  const isEditor = information?.role === Permission.Editor;
  const { isFeatureAllowed } = useFeatureGate();
  const hasWorkflowRbac = isFeatureAllowed('workflow-rbac');
  const { workflows, loading } = useWorkflowList(isAdmin, backendAvailable);
  const [selectedWorkflowId, setSelectedWorkflowId] = useState<string | null>(
    null
  );
  const [searchQuery, setSearchQuery] = useState('');
  const sortedWorkflows = useMemo(
    () => [...workflows].sort((a, b) => a.name.localeCompare(b.name)),
    [workflows]
  );
  const filteredWorkflows = useMemo(() => {
    const query = searchQuery.trim().toLowerCase();
    if (!query) return sortedWorkflows;
    return sortedWorkflows.filter(workflow => {
      return (
        workflow.name.toLowerCase().includes(query) ||
        workflow.id.toLowerCase().includes(query)
      );
    });
  }, [searchQuery, sortedWorkflows]);

  return (
    <SettingsSearchableEntityPage
      title={
        t['com.streamlain.settings.workspace.workflows.title']?.() ??
        'Workflow rights'
      }
      subtitle={
        t[
          'com.streamlain.settings.workspace.workflows.section.description'
        ]?.() ?? 'Manage access rights for each workflow in this workspace.'
      }
      headerAction={<SettingsOfflineHeaderAction show={!backendAvailable} />}
      searchValue={searchQuery}
      onSearchChange={setSearchQuery}
      searchPlaceholder={
        t[
          'com.streamlain.settings.workspace.workflows.search.placeholder'
        ]?.() ?? 'Workflows suchen...'
      }
      loading={loading && backendAvailable}
      hasItems={filteredWorkflows.length > 0}
      loadingContent={
        <SettingsEntityMessage>
          {t['com.streamlain.settings.workspace.workflows.loading']()}
        </SettingsEntityMessage>
      }
      emptyContent={
        <SettingsEntityMessage>
          {searchQuery.trim()
            ? (t[
                'com.streamlain.settings.workspace.workflows.search.empty'
              ]?.() ?? 'Keine Workflows fuer diese Suche gefunden.')
            : !backendAvailable
              ? offlineLabel
              : t['com.streamlain.settings.workspace.workflows.empty']()}
        </SettingsEntityMessage>
      }
      footer={isEditor ? <SettingsEditorScopeHint /> : null}
    >
      <SettingsEntityList>
        {filteredWorkflows.map(workflow => (
          <WorkflowListEntry
            key={workflow.id}
            workflow={workflow}
            expanded={selectedWorkflowId === workflow.id}
            onToggle={() =>
              setSelectedWorkflowId(
                selectedWorkflowId === workflow.id ? null : workflow.id
              )
            }
            hasWorkflowRbac={hasWorkflowRbac}
          />
        ))}
      </SettingsEntityList>
    </SettingsSearchableEntityPage>
  );
};
